import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { AuthCard } from '@/components/auth-card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authClient } from '@/lib/auth-client'

const MIN_PASSWORD_LENGTH = 10

export const Route = createFileRoute('/signup')({
  component: SignupPage,
})

function SignupPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const passwordTooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setPending(true)
    setError(null)

    const { error: signUpError } = await authClient.signUp.email({ name, email, password })
    setPending(false)

    if (signUpError) {
      setError(signUpError.message ?? 'Could not create account')
      return
    }

    // A personal workspace is created server-side during signup, so there is
    // no onboarding step between here and the sites list.
    queryClient.clear()
    navigate({ to: '/' })
  }

  return (
    <AuthCard
      title="Create your account"
      description="Your workspace is set up automatically."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="text-link underline-offset-4 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoComplete="name"
            required
            autoFocus
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            required
            aria-invalid={passwordTooShort}
          />
          <p className="min-h-4 text-[12px] text-muted-foreground">
            {passwordTooShort ? `At least ${MIN_PASSWORD_LENGTH} characters` : ''}
          </p>
        </div>

        {error && <p className="text-[12px] text-destructive">{error}</p>}

        <Button type="submit" className="w-full" disabled={pending || passwordTooShort}>
          {pending ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </AuthCard>
  )
}
